import { type FC, useMemo } from 'react';

import { type Scalars } from '../../../../__generated-gql-types__/globalTypes';

import {
  BaseScheduleSubmissionModalLayout,
  type BaseScheduleSubmissionModalLayoutProps,
} from './BaseScheduleSubmissionModalLayout';
import { type CategorySelectionCoachFragment } from './__generated-gql-types__/CategorySelection.generated';

interface CategorySelectionProps
  extends BaseScheduleSubmissionModalLayoutProps {
  coach: CategorySelectionCoachFragment;
  categorySelection: Nullable<Scalars['CategoryUrn']['output']>;
  setCategorySelection: ({
    id,
    slug,
  }: {
    id: Nullable<Scalars['CategoryUrn']['output']>;
    slug: Nullable<string>;
  }) => void;
  onMoveToSubmissionStep: (isComingFromCategorySelection?: boolean) => void;
}

export const CategorySelection: FC<CategorySelectionProps> = ({
  coach,
  categorySelection,
  setCategorySelection,
  onMoveToSubmissionStep,
  ...BaseScheduleSubmissionModalLayoutProps
}) => {
  const localCategorySelection = useMemo(
    () =>
      coach.categoryInformationList?.find(
        (c) => c.category.id === categorySelection,
      )?.category ?? null,
    [coach.categoryInformationList, categorySelection],
  );

  return (
    <BaseScheduleSubmissionModalLayout
      coach={coach}
      {...BaseScheduleSubmissionModalLayoutProps}
      buttonProps={{
        label: 'Continue',
        onClick: () => onMoveToSubmissionStep(true),
        disabled: !localCategorySelection,
      }}
      sidebarOptions={{
        title: 'Select the offerings you’re interested in.',
        subtitle:
          'Select all the options you might be interested in. Your coach will talk you through each of these options during your intro call.',
      }}
    >
      <div className="w-full grow overflow-scroll bg-leland-gray-hover px-10 py-6">
        <h2 className="text-xl font-medium">
          Which of {coach.user.firstName}’s categories are you interested in?
        </h2>
        <p className="text-leland-gray-light">
          This will help your coach understand your goals and sets your session
          up for success.
        </p>
        <div className="mt-4 flex w-full flex-col gap-3 sm:grid sm:grid-cols-2">
          {coach.categoryInformationList?.map((cil) => {
            const isSelected = localCategorySelection?.id === cil.category.id;
            return (
              <button
                className={`flex items-center gap-3 rounded-lg border bg-leland-white p-5${isSelected ? ' border-leland-gray-dark shadow-border shadow-leland-gray-dark' : ''}`}
                key={cil.id}
                onClick={() =>
                  setCategorySelection({
                    id: cil.category.id,
                    slug: cil.category.slug,
                  })
                }
              >
                <input
                  className={`form-radio mr-2 rounded`}
                  type="radio"
                  name="category"
                  value={cil.category.name}
                  checked={isSelected}
                />
                <label className="text-left text-lg font-medium">
                  {cil.category.name}
                </label>
              </button>
            );
          })}
        </div>
      </div>
    </BaseScheduleSubmissionModalLayout>
  );
};
